import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "@react-hook/media-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import axios from "axios";
import Modal from "react-modal";
import io from "socket.io-client";
import { useAuth0 } from "@auth0/auth0-react";

import AddEvent from "./AddEvent";

import styles from "./Calender.module.css";
import "./FullCalenderCss.css";

const generateRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 3; i++) {
    color += letters[Math.floor(Math.random() * 8)]; // Limit to darker shades (0-7)
  }
  return color;
};

const generateDayEvents = (events) => {
  return events.map((event) => {
    const eventDate = new Date(event.eventDate);
    const yearMonthDay = eventDate.toISOString().slice(0, 10);
    const startTime = event.start_time
      ? event.start_time.toString().padStart(2, "0")
      : "00";
    const endTime = event.end_time
      ? event.end_time.toString().padStart(2, "0")
      : "00";

    return {
      id: event.id,
      title: event.name,
      start: `${yearMonthDay}T${startTime}:00:00`,
      end: `${yearMonthDay}T${endTime}:00:00`,
      backgroundColor: generateRandomColor(),
      deleteId: event.deleteId,
      userId: event.userId,
      eventDate: event.eventDate,
    };
  });
};

const Calendar = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [scheduledEvents, setScheduledEvents] = useState([]);

  const socketRef = useRef(null); // Ref to hold the Socket.IO client instance

  const calendarRef = useRef(null);

  const { user, getAccessTokenSilently } = useAuth0(); // Destructure 'user' from Auth0 hook
  const [token, setToken] = useState(null);

  const isMobileScreen = useMediaQuery("(max-width: 765px)");

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUDIENCE, // Replace with your API audience
        });
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setToken(token);
      } catch (e) {
        console.error("Error setting Auth0 token:", e);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  // Fetch scheduled data on component load
  useEffect(() => {
    if (!token) return; // Wait for the token to be fetched

    socketRef.current = io(process.env.REACT_APP_BACKEND_URL, {
      query: {
        userId: user.sub, // Send user ID as a query parameter
      },
      auth: {
        token, // Send the token as an authentication header
      },
    });

    socketRef.current.on("connect", () => {});

    socketRef.current.on("initialScheduledData", (initialEvents) => {
      setScheduledEvents(initialEvents);
    });

    // Add this code to listen for the "allScheduledEvents" event
    socketRef.current.on("allScheduledEvents", (allScheduledEvents) => {
      setScheduledEvents(allScheduledEvents);
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, [user.sub, token]);

  const handleEventClick = (eventClickInfo) => {
    const clickedEvent = eventClickInfo.event;

    // Extract the deleteid of the clicked event
    const deleteId = clickedEvent.extendedProps.deleteId;
    const userId = clickedEvent.extendedProps.userId;

    // Now you can set the event to delete and perform other actions as needed
    setEventToDelete({ userId, deleteId });
  };

  const handleDeleteEvent = async () => {
    try {
      if (eventToDelete) {
        const deleteId = eventToDelete.deleteId;
        const userId = eventToDelete.userId;

        // Emit the deleteEvent socket event with the deleteId, userId, and currentDay as payload
        socketRef.current.emit("deleteEventWithData", {
          deleteId,
          userId,
        });

        // You can also remove the event locally without waiting for a server response
        const updatedEvents = scheduledEvents.filter(
          (event) => event.deleteId !== eventToDelete.deleteId
        );
        setScheduledEvents(updatedEvents);

        // Now, you can close the modal
        setEventToDelete(null);
      } else {
        console.error("No event to delete.");
      }
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  const closeModal = () => {
    setEventToDelete(null);
  };

  const eventsForCalendar = generateDayEvents(scheduledEvents);

  // Function to format the time (e.g., 01:30 PM)
  const computeDuration = (start, end) => {
    return (new Date(end) - new Date(start)) / (1000 * 60 * 60); // Convert difference from milliseconds to hours
  };

  const formatDuration = (start, end) => {
    const duration = computeDuration(start, end);
    return `You have ${duration.toFixed(2)} hours today.`;
  };

  return (
    <>
      <Helmet>
        <title>Calender Section</title>
        <meta name="description" content="Schedule your task in real time" />
      </Helmet>
      <section className={styles.container}>
        <div className={styles.calendarGrid}>
          <FullCalendar
            ref={calendarRef}
            events={eventsForCalendar}
            plugins={[dayGridPlugin, timeGridPlugin]}
            headerToolbar={{
              start: "today prev,next,addEvent",
              center: "title",
              end: isMobileScreen
                ? "timeGridWeek,timeGridDay"
                : "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            customButtons={{
              addEvent: {
                text: "Add Event",
                click: () => setModalOpen(true),
              },
            }}
            height={"80vh"}
            initialView={isMobileScreen ? "timeGridDay" : "dayGridMonth"}
            eventClick={(eventClickInfo) => handleEventClick(eventClickInfo)}
            eventContent={(eventInfo) => {
              return (
                <div
                  className="custom-event"
                  style={{
                    color: "#FFFFFF",
                    backgroundColor: generateRandomColor(),
                    borderRadius: "5px", // Rounded edges
                    padding: "4px", // Small spacing between both sides
                    width: "100%", // Span the entire width of the container
                    overflow: "hidden", // Hide overflow text if it doesn't fit
                    textOverflow: "ellipsis", // Show ellipsis for overflow text
                  }}
                >
                  <p>{eventInfo.event.title}</p>
                  <p>
                    {formatDuration(eventInfo.event.start, eventInfo.event.end)}
                  </p>
                </div>
              );
            }}
          />
        </div>
        <div></div>

        <Modal
          isOpen={eventToDelete !== null}
          onRequestClose={closeModal}
          className={styles.modalContainer}
          overlayClassName={styles.modalOverlay}
          contentLabel="Delete Confirmation"
          style={{
            overlay: {
              zIndex: 1000,
            },
            content: {
              zIndex: 1001,
            },
          }}
        >
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>Confirm Event Deletion</h2>
            <p className={styles.modalMessage}>
              Are you sure you want to delete this event?
            </p>
            <div className={styles.modalButtons}>
              <button
                className={`${styles.modalButton} ${styles.modalButtonConfirm}`}
                onClick={handleDeleteEvent}
              >
                Yes, Delete
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonCancel}`}
                onClick={closeModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        <AddEvent isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      </section>
    </>
  );
};

export default Calendar;
